import { library } from '@fortawesome/fontawesome-svg-core';
import { faDownload, faList, faSortAmountDown, faTh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Button, Dropdown, ListDivider, Menu, MenuButton, MenuItem } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { RowLayout } from '../../../../modules/common/layouts/rowLayout/RowLayout';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { EnableFor } from '../../../containers/EnableForRole';
import { nameOf } from '../../../helpers/object.helpers';
import { ListViewMode } from '../../user/user.store';
import { useDatasetPermissions } from '../datasets.permissions';
import { IDatasetDetailsImage } from '../datasetsDetails.store';
import { DatasetStatus } from '../datasetStatus.model';

library.add(faSortAmountDown, faTh, faList, faDownload);

interface IProps {
  viewMode: ListViewMode;
  orderBy: string;
  orderDirection: string;
  showBulkDeleteConfirmation: boolean;
  selectedImagesCount: number;
  usedImagesCount: number;
  datasetStatus: DatasetStatus;
  datasetCreatorId: string;
  onViewModeChange: (mode: ListViewMode) => Promise<void>;
  onOrderByChange: (order: string) => Promise<void>;
  onOrderDirectionChange: (direction: string) => Promise<void>;
  onBulkDelete: () => Promise<void>;
  toggleConfirmationModal: () => void;
  downloadImages: () => Promise<void>;
}

export const DatasetDetailsImageListControls = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const permissions = useDatasetPermissions();

  const isAnyImageSelected = props.selectedImagesCount > 0;
  const deleteConfirmationBody =
    props.usedImagesCount > 0 && !permissions.canDeleteMarkedImages().allowed
      ? 'delete_images_confirmation_body_with_used_images_count'
      : 'delete_images_confirmation_body';

  return (
    <RowLayout size='sm'>
      <>
        <div className='image-view-control'>
          <EnableFor hasPermission={permissions.canDownloadImages(props.datasetStatus, props.datasetCreatorId)}>
            <Button
              variant='outlined'
              color='primary'
              title={t('download_selected_images')}
              disabled={!isAnyImageSelected}
              onClick={() => {
                void props.downloadImages();
              }}
            >
              <FontAwesomeIcon icon={faDownload} />
            </Button>
          </EnableFor>
        </div>
        <div className='image-view-control'>
          <EnableFor hasPermission={permissions.canDeleteUnmarkedImages(props.datasetStatus, props.datasetCreatorId)}>
            <Button
              variant='outlined'
              color='primary'
              title={t('delete_selected_images')}
              disabled={!isAnyImageSelected}
              onClick={props.toggleConfirmationModal}
            >
              <TrashIcon />
            </Button>
          </EnableFor>
        </div>
        {props.viewMode === ListViewMode.Tiles && (
          <Dropdown>
            <MenuButton
              slots={{ root: Button }}
              slotProps={{ root: { variant: 'outlined', color: 'primary' } }}
            >
              <FontAwesomeIcon icon={faSortAmountDown} />
            </MenuButton>
            <Menu>
              <MenuItem
                onClick={(): void => {
                  void props.onOrderByChange(nameOf<IDatasetDetailsImage>('name'));
                }}
                selected={props.orderBy === nameOf<IDatasetDetailsImage>('name')}
              >
                {t('name')}
              </MenuItem>
              <MenuItem
                onClick={(): void => {
                  void props.onOrderByChange(nameOf<IDatasetDetailsImage>('createdDate'));
                }}
                selected={props.orderBy === nameOf<IDatasetDetailsImage>('createdDate')}
              >
                {t('createdDate')}
              </MenuItem>
              <MenuItem
                onClick={(): void => {
                  void props.onOrderByChange(nameOf<IDatasetDetailsImage>('width'));
                }}
                selected={props.orderBy === nameOf<IDatasetDetailsImage>('width')}
              >
                {t('image_width')}
              </MenuItem>
              <MenuItem
                onClick={(): void => {
                  void props.onOrderByChange(nameOf<IDatasetDetailsImage>('height'));
                }}
                selected={props.orderBy === nameOf<IDatasetDetailsImage>('height')}
              >
                {t('image_height')}
              </MenuItem>
              <MenuItem
                onClick={(): void => {
                  void props.onOrderByChange(nameOf<IDatasetDetailsImage>('annotationsCount'));
                }}
                selected={props.orderBy === nameOf<IDatasetDetailsImage>('annotationsCount')}
              >
                {t('annotated')}
              </MenuItem>
              <MenuItem
                onClick={(): void => {
                  void props.onOrderByChange(nameOf<IDatasetDetailsImage>('acceptedAnnotationsCount'));
                }}
                selected={props.orderBy === nameOf<IDatasetDetailsImage>('acceptedAnnotationsCount')}
              >
                {t('accepted')}
              </MenuItem>
              <MenuItem
                onClick={(): void => {
                  void props.onOrderByChange(nameOf<IDatasetDetailsImage>('size'));
                }}
                selected={props.orderBy === nameOf<IDatasetDetailsImage>('size')}
              >
                {t('image_size')}
              </MenuItem>
              <ListDivider />
              <MenuItem
                onClick={(): void => {
                  void props.onOrderDirectionChange('ASC');
                }}
                selected={props.orderDirection.toUpperCase() === 'ASC'}
              >
                {t('ascending')}
              </MenuItem>
              <MenuItem
                onClick={(): void => {
                  void props.onOrderDirectionChange('DESC');
                }}
                selected={props.orderDirection.toUpperCase() === 'DESC'}
              >
                {t('descending')}
              </MenuItem>
            </Menu>
          </Dropdown>
        )}
      </>
      <div className='image-view-control'>
        <Button
          variant='outlined'
          color='primary'
          onClick={(): void => {
            void props.onViewModeChange(props.viewMode === ListViewMode.Tiles ? ListViewMode.List : ListViewMode.Tiles);
          }}
        >
          <FontAwesomeIcon icon={props.viewMode === ListViewMode.Tiles ? faList : faTh} />
        </Button>
      </div>

      <ConfirmationModal
        confirmationHeader={'delete_images_confirmation'}
        confirmationQuestion={deleteConfirmationBody}
        confirmationQuestionValues={{ selectedImagesCount: props.selectedImagesCount, usedImagesCount: props.usedImagesCount }}
        onCancel={props.toggleConfirmationModal}
        onConfirm={props.onBulkDelete}
        showModal={props.showBulkDeleteConfirmation}
        confirmationYes={'delete'}
      />
    </RowLayout>
  );
});
